import Link from 'components/Basic/Link/Link';
import AddToCart from 'components/Blocks/Product/AddToCart/AddToCart';
import { AddToConfigurator } from 'components/Blocks/Product/AddToCart/AddToConfigurator';
import { SpinboxAddToCart } from 'components/Blocks/Product/AddToCart/SpinboxAddToCart';
import WatchDog from 'components/Blocks/Product/WatchDog/WatchDog';
import Button from 'components/Forms/Button/Button';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import { useRouter } from 'next/dist/client/router';
import { ExtendedConfiguratorProductApi } from 'types/configurator';
import { GtmListNameType } from 'types/gtm';
import { ProductActionType } from 'types/product';
import { onClickProductDetailGtmEventHandler } from 'utils/Gtm/EventHandlers';

type ProductActionProps = {
    idSuffix?: string;
    product: ProductActionType;
    gtmListName: GtmListNameType;
    listIndex: number;
    isConfigurator?: boolean;
};

const ProductAction: FC<ProductActionProps> = ({ idSuffix, gtmListName, listIndex, product, isConfigurator }) => {
    const testIdentifier = 'blocks-product-action';

    const router = useRouter();
    const t = useTypedTranslationFunction();
    const { type: domainType, url } = useDomainConfig();

    const productButtonType = (() => {
        if (isConfigurator) {
            return 'configurator' as const;
        }
        if (domainType === 'web') {
            return 'web' as const;
        }
        if (product.isMainVariant) {
            return 'choose-color' as const;
        }
        if (domainType === 'b2b') {
            return 'b2b' as const;
        }
        if (product.isSellingDenied) {
            return 'watch-dog' as const;
        }

        return 'add-to-cart' as const;
    })();

    return (
        <span className="mt-3 lg:mx-5 lg:mb-4 vl:mx-6" data-testid={testIdentifier}>
            {productButtonType === 'web' && (
                <Link
                    title={`${t('Show product details')} ${product.fullName}`}
                    href={product.slug}
                    onClick={() => onClickProductDetailGtmEventHandler(product, gtmListName, listIndex, url)}
                    isButton
                    size="smallNarrow"
                    type="product"
                >
                    {t('Show')}
                </Link>
            )}
            {productButtonType === 'add-to-cart' && (
                <AddToCart
                    productUuid={product.uuid}
                    gtmListName={gtmListName}
                    listIndex={listIndex}
                    quantity={product.b2bMinimumProductOrder ?? 1}
                    isBookingOnly={product.maximumOrderableQuantity <= 0}
                />
            )}
            {productButtonType === 'choose-color' && (
                <Button
                    aria-label={`${t('Choose color of product')} ${product.fullName}`}
                    size="smallNarrow"
                    onClick={() => router.push(product.slug)}
                    name={productButtonType}
                    data-testid={`${testIdentifier}-${productButtonType}`}
                >
                    {t('Choose color')}
                </Button>
            )}
            {productButtonType === 'watch-dog' && (
                <WatchDog productUuid={product.uuid} productName={product.fullName} />
            )}
            {productButtonType === 'configurator' && (
                // TODO: Refactor ProductAction call
                // eslint-disable-next-line
                // @ts-ignore: TS2352 - Suppress type assertion error
                <AddToConfigurator product={product as ExtendedConfiguratorProductApi} />
            )}
            {productButtonType === 'b2b' && (
                <SpinboxAddToCart
                    spinboxIdSuffix={idSuffix}
                    product={product}
                    gtmListName={gtmListName}
                    listIndex={listIndex}
                    variant="iconOnly"
                />
            )}
        </span>
    );
};

export default ProductAction;
